'use client';
import React, { useEffect, useState } from 'react';
import styled from '@benzinga/themetron';

import { Icon, Text } from '@benzinga/core-ui';
import { BzImage } from '@benzinga/image';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';
import { EntityAttributeDisplay } from './EntityAttributeDisplay';
import { Entity, EntityTag } from '@benzinga/content-manager';
import { Ticker } from '@benzinga/ticker-ui';
import { SessionContext } from '@benzinga/session-context';
import { DelayedQuote, QuotesManager } from '@benzinga/quotes-manager';
import { Ticker as TickerType } from '@benzinga/session';
import { QuoteChange, QuoteChangePercent } from '@benzinga/quotes-ui';
import { formatRoundFixed } from '@benzinga/utils';
import { sanitizeHTML } from '@benzinga/frontend-utils';

export interface EntityCardProps {
  entity: Entity;
  onTagClick?: (label: string, value: string | null) => void;
  options?: {
    hide_image?: boolean;
    full_desc?: boolean;
    show_more_button?: boolean;
  };
  variant?: 'default' | 'nano' | 'compact' | 'wide' | 'wide-image' | 'etf';
  showMoreButton?: boolean;
  initialQuotes?: Record<string, DelayedQuote>;
}

const EntityInfo = ({ entity, ticker }) => {
  return (
    <div className="entity-info-heading-text">
      <div className="flex">
        <div className="title-wrapper">
          <h3>{entity.ticker}</h3>
          <h6>{entity.name}</h6>
        </div>
      </div>
      <div className="flex justify-between pt-2 fund-price">
        <div>${formatRoundFixed(ticker?.lastTradePrice, 2)}</div>
        <div>
          {typeof ticker?.change === 'number' && (
            <QuoteChange absoluteChange={parseFloat(formatRoundFixed(ticker?.change, 2))} />
          )}
          {typeof ticker?.changePercent === 'number' && (
            <QuoteChangePercent percentageChange={ticker?.changePercent} showBrackets={true} />
          )}
        </div>
      </div>
      {entity.subtitle && <p className="entity-subtitle">{entity.subtitle}</p>}
      {entity.owner &&
        (entity.url ? (
          <a href={entity.url}>
            <div className="entity-company">{entity.owner.name}</div>
          </a>
        ) : (
          <div className="entity-company">{entity.owner.name}</div>
        ))}
    </div>
  );
};

export const EntityTagButton = ({ onClick, tag }) => {
  if (!tag.value) return null;
  return (
    <button className={`entity-tag ${tag.variant ?? 'default'}`} onClick={onClick}>
      {tag.value}
    </button>
  );
};

const placeholder = '/next-assets/images/benzinga-post-card-gray-placeholder.svg';

const EntityImage = ({ entity, variant }) => {
  const sizes =
    variant === 'compact'
      ? '(max-width: 800px) 80px, (min-width: 800px) 80px'
      : '(max-width: 800px) 240px, (min-width: 800px) 360px';
  return (
    <Thumbnail className="card-image">
      <BzImage
        height="100%"
        layout="fill"
        objectFit="contain"
        sizes={sizes}
        src={entity.image || placeholder}
        width="100%"
      />
    </Thumbnail>
  );
};

export const EntityCard: React.FC<EntityCardProps> = ({
  entity,
  initialQuotes,
  onTagClick,
  options,
  showMoreButton,
  variant,
}) => {
  const [fundData, setFundData] = useState<TickerType | null>(() => {
    if (initialQuotes && entity?.ticker && initialQuotes[entity.ticker]) {
      return initialQuotes[entity.ticker];
    }
    return null;
  });
  const session = React.useContext(SessionContext);
  const quoteManager = session.getManager(QuotesManager);
  const [isTextExpanded, setIsTextExpanded] = React.useState(false);

  useEffect(() => {
    (async () => {
      if (fundData) return;
      if (entity?.ticker) {
        const quotesRes = await quoteManager.getDelayedQuotes([entity.ticker]);
        const quote = quotesRes?.ok ? quotesRes?.ok?.[entity.ticker] : null;
        setFundData(quote);
      }
    })();
  }, [entity.ticker, fundData, quoteManager]);

  const handleClickTag = (tag: EntityTag) => {
    onTagClick && onTagClick(tag.type, tag.value);
  };

  const handleToggleShowMore = () => setIsTextExpanded(!isTextExpanded);

  return (
    <EntityCardContainer className={`entity-card ${variant}`}>
      {!options?.hide_image && entity.image && variant !== 'etf' && (
        <div className={`entity-image-container`}>
          {variant !== 'compact' && <EntityImage entity={entity} variant={variant} />}
        </div>
      )}

      <div className="card-info-container">
        <div className="entity-info">
          <div className="entity-info-container">
            <div className="entity-info-box">
              <div className="entity-info-heading">
                {entity.image && variant === 'compact' && !options?.hide_image && (
                  <EntityImage entity={entity} variant={variant} />
                )}
                <EntityInfo entity={entity} ticker={fundData} />
              </div>

              {variant !== 'nano' && entity.tags && (
                <div className="tags-container">
                  {entity.tags.map((tag: EntityTag) => (
                    <EntityTagButton key={tag.value} onClick={() => handleClickTag(tag)} tag={tag} />
                  ))}
                </div>
              )}

              {(variant === 'wide' || variant === 'wide-image' || variant === 'etf') && (
                <div className="desc flex flex-col">
                  {entity.description && (
                    <>
                      {entity.ticker && <h5>What is {entity.ticker}? </h5>}
                      <Text
                        className="desc-text"
                        dangerouslySetInnerHTML={{ __html: sanitizeHTML(entity.description) }}
                        lines={isTextExpanded ? undefined : options?.full_desc ? 20 : 3}
                      />
                    </>
                  )}
                  {showMoreButton && (
                    <button className="text-blue-500 text-sm inline-flex" onClick={handleToggleShowMore}>
                      Show {isTextExpanded ? 'Less' : 'More'}
                    </button>
                  )}
                  {/* {entity.url && (
                    <a className="read-more-button" href={entity.url} rel="noopener noreferrer" target="_blank">
                      READ MORE
                    </a>
                  )} */}
                </div>
              )}

              {entity.attributes && variant !== 'nano' && variant !== 'etf' && (
                <div className="attrs">
                  {entity.attributes[0] && <EntityAttributeDisplay attribute={entity.attributes[0]} />}
                  {entity.attributes[1] && <EntityAttributeDisplay attribute={entity.attributes[1]} />}
                  {entity.attributes[2] && (variant === 'wide' || variant === 'wide-image') && (
                    <EntityAttributeDisplay attribute={entity.attributes[2]} />
                  )}
                  {entity.attributes[3] && (variant === 'wide' || variant === 'wide-image') && (
                    <EntityAttributeDisplay attribute={entity.attributes[3]} />
                  )}
                </div>
              )}
              {entity.attributes && variant === 'etf' && (
                <div className="attrs">
                  {entity.attributes[2] && <EntityAttributeDisplay attribute={entity.attributes[2]} />}
                  {entity.attributes[3] && <EntityAttributeDisplay attribute={entity.attributes[3]} />}
                  {entity.attributes[4] && <EntityAttributeDisplay attribute={entity.attributes[4]} />}
                </div>
              )}
              {entity?.fund_holders?.length ? (
                <div className="top-holders">
                  <h5>Top Holdings:</h5>
                  <div className="etf-top-holder">
                    {entity?.fund_holders.slice(0, 3).map(holder => (
                      <Ticker key={holder.symbol} symbol={holder.symbol} />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="top-holders options-status">
                  {entity.attributes && entity.attributes[5] && (
                    <EntityAttributeDisplay attribute={entity.attributes[5]} />
                  )}
                </div>
              )}
            </div>
          </div>
          {(entity.affiliate_link || entity.url || entity.issuer_website) &&
            variant !== 'wide' &&
            variant !== 'wide-image' && (
              <a
                className="get-details-button"
                data-label="Offering Details"
                href={entity.affiliate_link || entity.url || entity.issuer_website}
                rel="noopener noreferrer"
                target="_blank"
              >
                Get Details <Icon icon={faArrowRight} />
              </a>
            )}
        </div>
      </div>
    </EntityCardContainer>
  );
};

const EntityCardContainer = styled.div`
  &.entity-card {
    width: 100%;
    overflow: hidden;
    border-radius: ${({ theme }) => theme.borderRadius.default};
    background-color: ${({ theme }) => theme.colorPalette.white};
    border: 1px solid ${({ theme }) => theme.colors.border};

    &:hover {
      box-shadow: ${({ theme }) => theme.shadow.sm};
    }

    &.wide,
    &.wide-image {
      display: inline-flex;
      .entity-image-container {
        min-width: 220px;
        min-height: 200px;
        .card-image {
          height: 100%;
          border-radius: 0;
        }
      }
      .tags-container {
        height: auto;
      }
      .card-info-container {
        width: 100%;
        .entity-info {
          h4 {
            max-width: 100%;
          }
        }
      }
    }
    &.wide-image {
      display: flex;
      flex-direction: column;
    }

    &.nano {
    }

    &.compact {
      .card-image {
        width: 80px;
        min-width: 80px;
        max-width: 80px;
        height: 70px;
        margin-right: 10px;
        border-radius: ${({ theme }) => theme.borderRadius.default};
        border: 1px solid ${({ theme }) => theme.colors.border};
      }

      .card-info-container {
        display: flex;
        flex-direction: column;

        h6 {
          margin-top: -5px;
        }

        .entity-info {
          h4 {
            white-space: normal;
            line-height: 1.3;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
          }
        }
      }

      .tags-container {
        height: auto;
      }
    }

    .entity-image-container {
      position: relative;
      &.has-profile-image {
        margin-bottom: 30px;
      }
      .entity-author-image {
        height: 100px;
        width: 100px;
        position: absolute;
        bottom: -15%;
        border-radius: 1000px;
        left: 20px;
        overflow: hidden;
      }
    }
    .ticker-wrapper {
      display: inline-block;
      vertical-align: middle;
    }
    .ticker-box-wrapper {
    }

    .tags-container {
      margin: 10px 0;
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      height: 60px;

      .entity-tag {
        font-size: ${({ theme }) => theme.fontSize.sm};
        font-weight: ${({ theme }) => theme.fontWeight.semibold};
        padding: 4px 8px;
        background-color: ${({ theme }) => theme.colorPalette.blue50};
        color: ${({ theme }) => theme.colorPalette.blue500};
        display: inline-block;
        border-radius: ${({ theme }) => theme.borderRadius.default};
        height: 30px;
        &.success {
          color: ${({ theme }) => theme.colorPalette.green500};
          border: 1px solid ${({ theme }) => theme.colorPalette.green500};
          background-color: ${({ theme }) => theme.colorPalette.green50};
        }
      }
    }

    .read-more-button {
      color: ${({ theme }) => theme.colorPalette.blue500};
      font-weight: bold;
    }

    .product-take {
      height: 4rem;
      position: relative;
      -webkit-box-orient: vertical;
      display: -webkit-box;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 100%;
      font-size: 0.9rem;
      -webkit-line-clamp: 2;
      .read-more-button {
        color: ${({ theme }) => theme.colorPalette.blue500};
        position: absolute;
        top: 2.7rem;
        width: 100%;
        text-align: center;
        left: 0;
        background-color: white;
        &:before {
          content: ' ';
          background: linear-gradient(transparent, white);
          display: block;
          height: 1.5em;
          margin-top: -1.5em;
          position: relative;
        }
      }
    }

    .entity-attr {
      .label {
        color: ${({ theme }) => theme.colorPalette.gray500};
        font-size: ${({ theme }) => theme.fontSize.base};
      }

      .value {
        color: ${({ theme }) => theme.colorPalette.gray700};
        font-size: 18px;
        font-weight: ${({ theme }) => theme.fontWeight.semibold};
      }
    }

    .right-attr .value {
      text-align: right;
    }

    .card-info-container {
      .entity-info {
        margin-top: 5px;

        h4 {
          font-size: ${({ theme }) => theme.fontSize.xl};
          font-weight: 700;
          margin-bottom: 0;
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
          text-transform: uppercase;
          color: ${({ theme }) => theme.colorPalette.gray700};
          max-width: 320px;
          margin-right: 0.5rem;
          vertical-align: middle;
          display: inline-block;
        }
        .entity-subtitle {
          font-size: ${({ theme }) => theme.fontSize.lg};
          color: ${({ theme }) => theme.colorPalette.gray900};
          margin: 0;
        }
        h6 {
          font-size: ${({ theme }) => theme.fontSize.lg};
          color: ${({ theme }) => theme.colorPalette.gray500};
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
          margin-bottom: 0;
        }

        .entity-company {
          font-size: 0.8rem;
          color: ${({ theme }) => theme.colorPalette.gray500};
        }
      }
    }

    .entity-info-box {
      padding: 10px 15px;
    }

    .entity-info-heading {
      .entity-info-heading-text {
        overflow: hidden;
        .title-wrapper {
          max-width: calc(100% - 75px);
        }
      }
    }

    .attrs {
      display: flex;
      justify-content: space-between;
      margin: 8px 0;

      > div {
        display: flex;
        flex-direction: column;
      }
    }

    .get-details-button {
      display: flex;
      width: 100%;
      background-color: ${({ theme }) => theme.colorPalette.blue500};
      padding: 10px;
      justify-content: space-between;
      align-items: center;
      color: #ffffff;
      font-weight: ${({ theme }) => theme.fontWeight.semibold};
    }

    &.etf {
      border-radius: 4px;
      border: 1px solid #ceddf2;
      box-shadow: 0px 2px 8px 0px rgba(25, 68, 128, 0.2);
      .card-info-container {
        height: 100%;
        .entity-info {
          height: 100%;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          margin: 0;
        }
      }
      .entity-info-box {
        padding: 0;
        .entity-info-heading {
          padding: 12px 16px 8px;
          border-bottom: 1px solid #e1ebea;
          .entity-info-heading-text {
            .title-wrapper {
              max-width: 100%;
              h3 {
                font-size: 20px;
                font-weight: 700;
                line-height: 28px;
              }
              h6 {
                font-size: 14px;
                font-style: normal;
                font-weight: 400;
                line-height: 20px;
              }
            }
          }
          .fund-price {
            font-size: 18px;
            font-weight: 700;
            line-height: 28px;
            .quote-change-percent {
              padding-left: 4px;
            }
          }
        }
        .desc {
          padding: 12px 16px;
          color: #303a49;
          h5 {
            font-style: normal;
            font-weight: 700;
            line-height: 20px;
            padding-bottom: 8px;
          }
        }
        .attrs {
          margin: 0 0 8px;
        }
        .top-holders {
          padding: 12px 16px;
          border-top: 1px solid #e1ebea;
          h5 {
            color: #303a49;
            font-size: 14px;
            font-weight: 400;
            line-height: 20px;
            margin-bottom: 6px;
          }
          .etf-top-holder {
            display: flex;
            overflow: hidden;
            .ticker-wrapper {
              margin-right: 8px;
            }
            .ticker-box-wrapper {
              border: none;
              overflow: initial;
            }
          }
          &.options-status {
            .entity-attr {
              padding-left: 0;
              padding-right: 0;
              .value {
                color: #283d59 !important;
                font-size: 14px;
                font-weight: 600;
              }
            }
          }
        }
        .entity-attr {
          flex-basis: 100%;
          .label {
            color: #303a49;
            font-size: 14px;
            font-weight: normal;
          }
        }
      }
      .get-details-button {
        background-color: #fa8418;
      }
    }
  }
`;

const Thumbnail = styled.div`
  position: relative;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  height: 200px;
  width: 100%;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.background};
`;

// const VerifiedBadgeIcon = () => (
//   <svg
//     className="verified-badge"
//     height="560"
//     version="1.1"
//     viewBox="188.977 0 560 560"
//     width="560"
//     x="0"
//     xmlns="http://www.w3.org/2000/svg"
//     xmlSpace="preserve"
//     y="0"
//   >
//     <path
//       d="M748.977 293.125c0 19.578-4.704 37.734-14.11 54.36s-21.984 29.64-37.844 38.718c.438 2.953.657 7.547.657 13.782 0 29.64-9.953 54.796-29.64 75.578-19.798 20.89-43.642 31.28-71.532 31.28-12.469 0-24.39-2.296-35.657-6.89-8.75 17.938-21.328 32.375-37.843 43.422C506.602 554.531 488.555 560 468.977 560c-20.016 0-38.172-5.36-54.36-16.297-16.297-10.828-28.765-25.375-37.515-43.75-11.266 4.594-23.078 6.89-35.657 6.89-27.89 0-51.843-10.39-71.86-31.28-20.015-20.782-29.968-46.048-29.968-75.579 0-3.281.438-7.875 1.203-13.781-15.86-9.188-28.437-22.094-37.844-38.719-9.296-16.625-14-34.781-14-54.36 0-20.783 5.25-39.924 15.641-57.205s24.39-30.078 41.89-38.39c-4.593-12.47-6.89-25.047-6.89-37.516 0-29.531 9.953-54.797 29.969-75.578s43.969-31.281 71.86-31.281c12.468 0 24.39 2.296 35.656 6.89 8.75-17.937 21.328-32.375 37.843-43.422C431.352 5.575 449.398-.003 468.977-.003s37.625 5.578 54.03 16.516c16.407 11.047 29.095 25.484 37.845 43.422 11.265-4.594 23.078-6.89 35.656-6.89 27.89 0 51.734 10.39 71.531 31.28s29.64 46.047 29.64 75.579c0 13.78-2.077 26.25-6.233 37.515 17.5 8.313 31.5 21.11 41.89 38.39 10.39 17.392 15.64 36.532 15.64 57.316z"
//       fill="#00BCEF"
//     ></path>
//     <path
//       d="M325.72 310.034l30.842-38.964 77.92 61.689 138.79-175.322 38.96 30.843L442.6 402.56l-116.88-92.526z"
//       fill="#FFF"
//     ></path>
//   </svg>
// );

// const FlashIcon = () => (
//   <svg height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
//     <path d="M0 0h24v24H0z" fill="none" />
//     <path d="M13 9h8L11 24v-9H4l9-15v9zm-2 2V7.22L7.532 13H13v4.394L17.263 11H11z" fill="rgba(0,0,255,1)" />
//   </svg>
// );
